'use client';

import type { OnChangeFn } from '@tanstack/react-table';
import type { AccountMultiselectOption as AccountType } from 'src/features/tasks/creation/components/account-multiselect/account-multiselect-types';
import type { FragmentType } from 'src/gql';
import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { TableSkeleton } from '@savvy-kit/components/skeleton';
import { isNil } from 'es-toolkit';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { graphql, unmaskFragment } from 'src/gql';
import { getTaskCreationHref } from 'src/utils/tasks';

import { DataTable } from '@savvy/data-table';

import type { AccountsTableColumn } from './utils/accounts-columns';
import { dedupeItems, reorderColumnsFromIds } from '../shared/column-ordering';
import {
  AccountsDataTableRowFragment,
  createAccountsDataTableColumns,
  DATA_TABLE_COLUMN_ID_TO_NAME,
  DATA_TABLE_NAME_TO_COLUMN_ID,
} from './accounts-data-table-columns';
import { AccountsPaginationFooter } from './accounts-pagination-footer';
import { getDefaultAccountsColumns } from './utils/accounts-columns';
import { useAccountsParams } from './utils/accounts-url-state';

export const AccountsDataTableFragment = graphql(/* GraphQL */ `
  fragment AccountsDataTable on Query {
    advisorAccounts(
      first: $first
      after: $after
      last: $last
      before: $before
      advisorIds: $advisorIds
      balanceRange: $balanceRange
      strategyFilter: $strategyFilter
      feeSchedules: $feeSchedules
      householdIds: $householdIds
      includeUnassignedAdvisor: $includeUnassignedAdvisor
      portfolioMustBePresent: $portfolioMustBePresent
      providerNames: $providerNames
      sortField: $sortField
      sortDirection: $sortDirection
      query: $query
    ) {
      ...AccountsPaginationFooter
      edges {
        node {
          id
          ...AccountsDataTableRow
        }
      }
    }
  }
`);

type Props = {
  readonly queryFrag: FragmentType<typeof AccountsDataTableFragment> | undefined;
  readonly loading: boolean;
};

export function AccountsDataTable({ queryFrag, loading }: Props) {
  const router = useRouter();
  const { flagCashDashboard: cashDashboardEnabled } = useFlags();
  const { params, cycleCashMmSort, setSortBy, setColumns } = useAccountsParams();
  const data = unmaskFragment(AccountsDataTableFragment, queryFrag);

  const selectedColumns = useMemo<AccountsTableColumn[]>(() => {
    let cols = params.columns.length > 0 ? params.columns : getDefaultAccountsColumns();
    if (cashDashboardEnabled !== true) {
      cols = cols.filter((c) => c !== 'Cash & Money Market');
    }
    return cols.includes('Account Name') ? cols : ['Account Name', ...cols];
  }, [params.columns, cashDashboardEnabled]);

  const accounts = useMemo(() => {
    if (isNil(data?.advisorAccounts.edges)) return [];
    return data.advisorAccounts.edges
      .map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => !isNil(node))
      .map((node) => unmaskFragment(AccountsDataTableRowFragment, node));
  }, [data]);

  const handleStartEnhancedEnrollment = useCallback(
    (account: AccountType) => {
      const fullAccount = accounts.find((a) => a.id === account.id);
      router.push(getTaskCreationHref('swim', fullAccount?.portfolio?.household.salesforceAccountId));
    },
    [accounts, router],
  );

  const columns = useMemo(() => {
    const allColumns = createAccountsDataTableColumns({
      onStartEnhancedEnrollment: handleStartEnhancedEnrollment,
      sortBy: params.sortBy,
      sortByOrder: params.sortByOrder,
      onCycleCashMmSort: cycleCashMmSort,
      setSortBy,
    });

    const selection = new Set<AccountsTableColumn>(selectedColumns);
    const actionsCol = allColumns.find((col) => col.id === 'actions');
    const dataColumns = allColumns
      .filter((col) => col.id !== 'actions')
      .filter((col) => {
        const name = !isNil(col.id) ? DATA_TABLE_COLUMN_ID_TO_NAME[col.id] : undefined;
        return !isNil(name) && selection.has(name);
      })
      .sort((a, b) => {
        const nameA = DATA_TABLE_COLUMN_ID_TO_NAME[a.id ?? ''];
        const nameB = DATA_TABLE_COLUMN_ID_TO_NAME[b.id ?? ''];
        if (!nameA || !nameB) return 0;
        return selectedColumns.indexOf(nameA) - selectedColumns.indexOf(nameB);
      });
    return actionsCol ? [...dataColumns, actionsCol] : dataColumns;
  }, [handleStartEnhancedEnrollment, params.sortBy, params.sortByOrder, cycleCashMmSort, selectedColumns, setSortBy]);

  const columnOrder = useMemo(
    () => [
      ...dedupeItems(
        selectedColumns
          .map((column) => DATA_TABLE_NAME_TO_COLUMN_ID[column])
          .filter((columnId): columnId is string => !isNil(columnId)),
      ),
      'actions',
    ],
    [selectedColumns],
  );

  const handleColumnOrderChange: OnChangeFn<string[]> = useCallback(
    (updaterOrValue) => {
      const nextOrderIds = dedupeItems(
        typeof updaterOrValue === 'function' ? updaterOrValue(columnOrder) : updaterOrValue,
      );
      setColumns(
        reorderColumnsFromIds({
          nextOrderIds,
          columnIdToColumn: DATA_TABLE_COLUMN_ID_TO_NAME,
          visibleColumns: selectedColumns,
        }),
      );
    },
    [columnOrder, selectedColumns, setColumns],
  );

  if (loading) {
    return (
      <TableSkeleton
        rows={selectedColumns.length > 0 ? Math.min(params.pageSize, 10) : 8}
        columns={selectedColumns.length || 5}
      />
    );
  }

  return (
    <div>
      <DataTable
        columns={columns}
        data={accounts}
        features={{
          columnResizing: true,
          columnOrdering: true,
        }}
        columnOrder={columnOrder}
        onColumnOrderChange={handleColumnOrderChange}
        classNames={{
          root: 'border-0 rounded-none shadow-none',
          headerCell: '[&>div]:pl-0',
          row: ({ row }) => {
            const isNavigable = !isNil(row.original.portfolio?.household.salesforceAccountId);
            return isNavigable ? 'cursor-pointer' : '';
          },
        }}
        slotProps={{
          row: ({ row }) => {
            const salesforceAccountId = row.original.portfolio?.household.salesforceAccountId;
            const accountId = row.original.id;
            const isNavigable = !isNil(salesforceAccountId);

            return isNavigable
              ? {
                  onClick: () => {
                    void router.push(`/advisor/dashboard/client/${salesforceAccountId}/portfolio/account/${accountId}`);
                  },
                }
              : {};
          },
        }}
        emptyState={
          <div className="flex flex-col items-center justify-center py-8">
            <span className="text-sm text-secondary">No accounts found</span>
          </div>
        }
      />
      <AccountsPaginationFooter queryFrag={data?.advisorAccounts} />
    </div>
  );
}
